import { useEffect, useState } from 'react';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@verlo/shared/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { Loader2, ShieldAlert, Tag } from 'lucide-react';
import { authApi } from '@verlo/shared/services/api';

const AUTHORISING_ROLES = new Set([
  'shift_lead', 'manager', 'general_manager', 'area_manager', 'operations', 'owner',
]);
const LEGACY_AUTHORISING_ROLES = new Set(['manager', 'admin', 'owner', 'super_admin']);
const canAuthorise = (u) =>
  AUTHORISING_ROLES.has((u.rbac_role || '').toLowerCase())
  || LEGACY_AUTHORISING_ROLES.has((u.role || '').toLowerCase());

/**
 * The Offers row (promotions plan): the bill-level offers running at this site right
 * now, as tiles. A server taps one and it's on the bill — the office authorised the
 * offer when it set it up. An offer that wants a code asks for it; one marked "needs a
 * manager" collects a manager PIN when the operator can't self-authorise (`canDiscount`).
 * Line offers never appear here: the pricing pass applies them itself.
 */
const OffersDialog = ({
  open, onOpenChange, offers = [], canDiscount = false, siteId, onSubmit,
}) => {
  const [picked, setPicked] = useState(null);
  const [code, setCode] = useState('');
  const [managerId, setManagerId] = useState('');
  const [pin, setPin] = useState('');
  const [managers, setManagers] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setPicked(offers.length === 1 ? offers[0] : null);
    setCode('');
    setManagerId('');
    setPin('');
    setError('');
  }, [open, offers]);

  const needsAuth = Boolean(picked?.needs_manager) && !canDiscount;

  useEffect(() => {
    if (!open || !needsAuth) return;
    let cancelled = false;
    setLoadingManagers(true);
    authApi.pinStaffGrid(siteId)
      .then((res) => { if (!cancelled) setManagers((res.data?.users || []).filter(canAuthorise)); })
      .catch(() => { if (!cancelled) setManagers([]); })
      .finally(() => { if (!cancelled) setLoadingManagers(false); });
    return () => { cancelled = true; };
  }, [open, needsAuth, siteId]);

  const noManagers = needsAuth && !loadingManagers && managers.length === 0;
  const canSubmit = !submitting && picked && !noManagers
    && (!picked.has_code || code.trim())
    && (!needsAuth || (managerId && pin.length >= 4));

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    const payload = { promotion_id: picked.id };
    if (picked.has_code) payload.code = code.trim();
    if (needsAuth) { payload.manager_id = managerId; payload.manager_pin = pin; }
    try {
      await onSubmit(payload);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403) setError(detail || 'Manager PIN not recognised');
      else if (status === 423 || status === 429) setError(detail || 'Too many tries. Wait a moment, then try again.');
      else setError(typeof detail === 'string' ? detail : "Couldn't apply the offer. Try again.");
      setPin('');
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !submitting && onOpenChange(v)}>
      <DialogContent size="confirm" data-testid="offers-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 text-verlo">
            <Tag size={18} />
            <DialogTitle>Offers</DialogTitle>
          </div>
          <DialogDescription>Running now. Tap one to put it on the bill.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {offers.length === 0 ? (
            <p className="text-sm text-ink-faint text-center py-4">No offers to tap right now.</p>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {offers.map((o) => {
                const on = picked?.id === o.id;
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => { setPicked(o); setError(''); }}
                    className={`min-h-[64px] text-left border p-3 transition-colors ${on ? 'border-verlo bg-verlo-tint' : 'border-hairline-strong bg-white hover:border-verlo'}`}
                    data-testid={`offer-tile-${o.id}`}
                  >
                    <p className="text-[14px] font-semibold leading-tight">{o.name}</p>
                    <p className="text-[12px] text-ink-faint mt-0.5">
                      {o.words}{o.needs_manager ? ' · needs a manager' : ''}
                    </p>
                  </button>
                );
              })}
            </div>
          )}

          {picked?.has_code && (
            <div className="space-y-1.5">
              <Label className="text-xs">Code</Label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="As printed on the voucher"
                className="uppercase tracking-wider"
                data-testid="offer-code"
                autoComplete="off"
              />
            </div>
          )}

          {needsAuth && (
            <div className="border border-hairline border-l-[3px] border-l-signal-warn-line bg-white p-3 space-y-3">
              <p className="font-heading text-[10.5px] font-bold uppercase tracking-[0.06em] text-signal-warn flex items-center gap-1.5">
                <ShieldAlert size={12} /> A manager needs to authorise this offer
              </p>
              <div className="space-y-1.5">
                <Label className="text-xs">Authorising manager</Label>
                <Select value={managerId} onValueChange={setManagerId} disabled={submitting || loadingManagers || noManagers}>
                  <SelectTrigger className="h-10 bg-white" data-testid="offer-manager">
                    <SelectValue placeholder={loadingManagers ? 'Loading…' : noManagers ? 'No manager on this site' : 'Select manager'} />
                  </SelectTrigger>
                  <SelectContent>
                    {managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Manager PIN</Label>
                <Input
                  type="password" inputMode="numeric" pattern="[0-9]*" placeholder="••••"
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                  maxLength={6}
                  className="text-center text-xl tracking-[0.4em] font-mono bg-white"
                  data-testid="offer-pin"
                  autoComplete="off"
                  disabled={submitting || noManagers}
                />
              </div>
            </div>
          )}

          {error && <p className="text-xs text-signal-stop text-center" data-testid="offer-error">{error}</p>}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button className="bg-verlo hover:bg-verlo-hover gap-1.5" onClick={submit} disabled={!canSubmit} data-testid="offer-submit">
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {picked ? `Apply ${picked.name}` : 'Apply offer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OffersDialog;
